import { createRouter, createWebHistory } from 'vue-router'
import { routes } from './routes'
import NProgress from 'nprogress/nprogress'
import { getCookie, setHeaders } from '@/utils/index'
import { PATH } from '@/constants'
import userService from '@/services/auth.service'
import { has, omit } from 'lodash'
import axios from 'axios'
import authService from '@/services/auth.service'

const snsLoginResultPath = ['/alumni/lobby', '/alumni/register_alumnus_community']

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to?.name === from?.name && to.params === from.params) {
      return {}
    }
    if (to?.path !== from?.path) {
      return {
        top: 0,
        left: 0,
      }
    }
  },
})

// Before each route evaluates...
router.beforeEach(async (routeTo: any, routeFrom, next) => {
  const path = PATH[`${routeTo.path.split('/')[1]}`]

  if (path?.header && getCookie(path.header)) {
    setHeaders(JSON.parse(getCookie(path.header)))
  }

  const title = routeTo?.meta?.title ?? 'Home'
  document.title = '' + title
  // If this isn't an initial page load...
  if (routeFrom.name !== null) {
    // Start the route progress bar.
    NProgress.start()
  }
  // Check if auth is required on this route
  // (including nested routes).
  const publicRoute = routeTo.meta.publicRoute

  // In case of login with SNS services, if the process of SNS authentication success,
  // login data will be return as header to only 2 destination (login & alumni invitation by link)
  // and auth_token is checked to make sure that authentication data is included
  if (snsLoginResultPath.includes(routeTo.path) && has(routeTo.query, 'otp_token')) {
    const { otp_token, two_factor_enabled } = routeTo.query

    // two_factor_enabled=true -> SA81 -> target url
    // two_factor_enabled=false -> SC70 -> edit profile -> logout -> SA81 -> target url
    if (two_factor_enabled === 'false') {
      const data = await authService.authenticationFromOtpToken({ otp_token })
      const { status } = data
      if (status === 200) {
        const { expiry, auth_token: authToken } = data.headers
        if (expiry) {
          axios.defaults.headers['X-CSRF-TOKEN'] = authToken
          let expires = `${expiry ? new Date(parseInt(expiry) * 1000).toUTCString() : new Date()['toGMTString']()}`
          const basePath = PATH.alumni
          const dataHeader = {
            'request-path': basePath.path,
            ...data.headers,
          }
          document.cookie = `${basePath.pathName}=${basePath.path}; expires=${expires}; path=/`
          document.cookie = `${basePath.header}=${JSON.stringify(omit(dataHeader, ['cache-control', 'content-type']))}; expires=${expires}; secure; path=/`
          // SC70 二段階認証未設定にリダイレクトさせる
          document.cookie = `${basePath.path}-required-two-factor=true; expires=${expires}; secure; path=/`
          setHeaders(dataHeader)
        }
        return next({
          replace: true,
          path: '/alumni/2fa_notification',
          query: omit(routeTo.query, 'otp_token', 'blank'),
        })
      }
    } else if (two_factor_enabled === 'true') {
      // 10 minutes expiration for cookie
      const now = new Date()
      now.setTime(now.getTime() + 10 * 60 * 1000)
      document.cookie = `${PATH.alumni.header}-one-time=${JSON.stringify(
        omit({ one_time_password: routeTo.query?.otp_token }, ['cache-control', 'content-type'])
      )}; expires=${now.toUTCString()}; secure; path=/`

      const query = routeTo.query?.invited_code ? { ...routeTo.query, redirect: `/alumni/register_alumnus_community?invited_code=${routeTo.query?.invited_code}` } : routeTo.query

      return next({
        replace: true,
        path: '/alumni/input_authentication_code',
        query: omit(query, 'otp_token', 'blank'),
      })
    }
    return next({
      replace: true,
      path: routeTo.path,
      query: omit(routeTo.query, 'otp_token', 'blank'),
    })
  }
  const section = routeTo.path.split('/')[1] // "community" | "alumni"
  const routeName = '' + routeTo?.name
  const hasHeader = !!getCookie(path?.header)
  const hasOneTime = !!getCookie(`${path?.header}-one-time`)

  const isTargetSection = ['community', 'alumni'].includes(section)

  // Define route groups for easier checks
  const loginPages = ['users login', 'login']
  const inputCodePages = ['community_input_authentication_code', 'alumni_input_authentication_code', ...loginPages, 'invitation', 'invite_url']
  const twoFaPages = ['community_2fa_notification', 'alumni_2fa_notification', ...loginPages, 'alumni-profile-edit', 'account profile edit', 'invitation', 'invite_url']
  if (isTargetSection) {
    // Case 1: No header cookie + no one-time cookie -> must go to login
    if (!hasHeader && !hasOneTime && !loginPages.includes(routeName)) {
      return redirectToLogin(next, publicRoute, routeTo)
    }

    // Case 2: No header cookie + has one-time cookie -> must go to input code page
    if (!hasHeader && hasOneTime && !inputCodePages.includes(routeName)) {
      document.cookie = `${section}-redirect-path=${routeTo.path}; path=/`
      return next({
        name: `${section}_input_authentication_code`,
        replace: true,
        query: { redirect: routeTo.fullPath },
      })
    }
  }
  return next()
})

const REGEX_CHUNK = /Loading chunk .* failed./
router.onError((error) => {
  if (REGEX_CHUNK.test(error.message)) {
    window.location.reload()
  }
})

function redirectToLogin(next, publicRoute, routeTo) {
  // Pass the original route to the login component
  if (!publicRoute)
    return next({
      name: routeTo.path.startsWith('/community/') ? 'users login' : 'login',
      replace: true,
      path: routeTo.path,
      query: { redirect: `${routeTo.fullPath}` },
    })
  return next()
}

router.beforeResolve(async (routeTo: any, routeFrom, next: any) => {
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve: any, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args: any[]) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (routeFrom.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done()
              }
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

// When each route is finished evaluating...
router.afterEach(async (to, from) => {
  // Set status read=1 by notice_user_id
  await getNotificationTypeApi(to)
  // Complete the animation of the route progress bar.
  NProgress.done()
})

/**
 * Set status read=1 by notice_user_id
 * @param routeTo
 */
const getNotificationTypeApi = async (routeTo: any) => {
  try {
    const screenId = routeTo?.meta?.screenId
    const listscreenId = ['SR40', 'SO65', 'OD65', 'OF10', 'SH20', 'CT10', 'CK10', 'SM65']
    if (listscreenId.includes(screenId) && routeTo?.query.notice_user_id) {
      await userService.getNotificationTypeApi(routeTo?.params.id, routeTo?.query.notice_user_id)
    }
  } catch (error) {
    console.log('err', error)
  }
}

export default router
